import type { Country } from '@tenders/shared';
import { countryConfig } from '../country';
import type { Locale } from '../i18n/index';

/**
 * Press mentions — third-party publications that wrote ABOUT the service.
 *
 * Rendered by `components/press-block.tsx` on `/about`. Data-driven on purpose:
 * the next mention is one more entry here and nothing else changes.
 *
 * ## Rules for adding an entry
 *
 * 1. **Verify every URL before committing it.** A press block that links to a
 *    404 is worse than no press block. Statuses recorded at the time of writing
 *    are noted per entry below.
 * 2. **Link the reader's own script.** gazeta.uz publishes each piece three
 *    times — `/ru/` (Russian), `/uz/` (Uzbek **Cyrillic**, `hreflang="uz-Cyrl"`)
 *    and `/oz/` (Uzbek **Latin**, `hreflang="uz-Latn"`). This product's Uzbek is
 *    Latin, so the `uz` URL here is the `/oz/` one. Linking `/uz/` would hand a
 *    Latin-script reader a Cyrillic page — the same class of mismatch the
 *    prerender encoding guard (`scripts/encoding-guard.ts`) exists to catch.
 * 3. **Titles are quoted verbatim from the publication**, in that publication's
 *    own wording — except the apostrophe, which is normalised to ASCII `'` to
 *    match the deployment-wide Uzbek standard (`i18n/uz-apostrophes.test.ts`;
 *    gazeta.uz's Latin edition uses U+2018).
 * 4. **`outlet` is a text wordmark, never the publisher's logo file.** Their
 *    mark is their trademark; a text label plus the date is what we publish.
 *    «Газета.uz» is the wordmark gazeta.uz sets as `og:site_name` on all three
 *    language editions, Latin one included — so it is not localised here.
 */
export interface PressMention {
  /** Stable key: outlet slug + publication date. Used as the React key. */
  id: string;
  /** Publication wordmark, as plain text. Not localised — see rule 4. */
  outlet: string;
  /** Publication date, ISO `YYYY-MM-DD`. Rendered via `formatDateNumeric`. */
  date: string;
  /** Headline as published, per locale. */
  title: Record<Locale, string>;
  /**
   * Article URL per locale. `uz` is required and is the fallback for any locale
   * the publication did not translate into.
   */
  url: { uz: string; ru?: string; kk?: string; es?: string };
  /**
   * Deployments this mention belongs to. Defaults to `['uz']` — same convention
   * as `content/guide-articles/index.ts`. A piece about UzTender is not press
   * for the KZ build, so it must not appear there.
   */
  countries?: Country[];
}

export const PRESS: PressMention[] = [
  {
    // kun.uz, 2026-09-28 10:39 (+05:00). Fetched 2026-09-28, HTTP 200, `lang="ru"`.
    // Russian edition only — kun.uz published no Uzbek version of this piece and
    // the page carries no hreflang alternates, so `uz` falls back to the Russian
    // URL and headline (the same fallback `kk` uses for the gazeta.uz entry).
    id: 'kun-uz-2026-09-28',
    outlet: 'Kun.uz',
    date: '2026-09-28',
    title: {
      ru: 'Первый госзаказ без связей: что показывают 85 тысяч контрактов',
      uz: 'Первый госзаказ без связей: что показывают 85 тысяч контрактов',
      kk: 'Первый госзаказ без связей: что показывают 85 тысяч контрактов',
      // UZ-only mention (`countries: ['uz']`): never rendered on the ES build.
      es: 'Первый госзаказ без связей: что показывают 85 тысяч контрактов',
    },
    url: {
      uz: 'https://kun.uz/ru/news/2026/09/28/pervyy-goszakaz-bez-svyazey-chto-pokazyvayut-85-tysyach-kontraktov',
      ru: 'https://kun.uz/ru/news/2026/09/28/pervyy-goszakaz-bez-svyazey-chto-pokazyvayut-85-tysyach-kontraktov',
    },
    countries: ['uz'],
  },
  {
    // gazeta.uz, 2026-09-23. All three editions fetched 2026-09-25, HTTP 200:
    //   ru  https://www.gazeta.uz/ru/2026/09/23/uztender/   (lang="ru")
    //   uz  https://www.gazeta.uz/uz/2026/09/23/uztender/   (lang="uz-Cyrl") — NOT linked
    //   oz  https://www.gazeta.uz/oz/2026/09/23/uztender/   (lang="uz-Latn") — linked as `uz`
    id: 'gazeta-uz-2026-09-23',
    outlet: 'Газета.uz',
    date: '2026-09-23',
    title: {
      ru: 'UzTender: половина победителей госзакупок — новички, но треть контрактов забирает 161 компания',
      uz: "UzTender: davlat xaridlari g'oliblarining yarmi — yangi kompaniyalar, 33 foiz kontraktni esa 161 kompaniya oldi",
      // No Kazakh edition of this piece exists; kk falls back to the Russian
      // headline, which is the language the KZ deployment defaults to anyway.
      kk: 'UzTender: половина победителей госзакупок — новички, но треть контрактов забирает 161 компания',
      // UZ-only mention (`countries: ['uz']`): never rendered on the ES build.
      es: 'UzTender: половина победителей госзакупок — новички, но треть контрактов забирает 161 компания',
    },
    url: {
      uz: 'https://www.gazeta.uz/oz/2026/09/23/uztender/',
      ru: 'https://www.gazeta.uz/ru/2026/09/23/uztender/',
    },
    countries: ['uz'],
  },
];

/** The mentions that belong to the running deployment. */
export function pressForCountry(): PressMention[] {
  return PRESS.filter((p) => (p.countries ?? ['uz']).includes(countryConfig.code));
}
